import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '../../base.component';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { EmailVerificatoinSdk } from '@sdks/email-verification.sdk';
import { catchError, EMPTY, of, takeUntil, tap } from 'rxjs';
import { DownloadSdk } from '@sdks/download.sdk';
import { ProductionJobStatus } from '@models/review/production-job.model';
import { ProductionJobDownloadSharedManager } from '@managers/production-job-download-shared.manager';
import { ProductionJobSharedDetail } from '@models/ui/production-job-shared-detail.model';

@Component({
    selector: 'lex-user-confirm-download',
    templateUrl: './user-confirm-download.component.html',
    styleUrl: './user-confirm-download.component.scss',
    standalone: false
})
export class UserConfirmDownloadComponent extends BaseComponent implements OnInit {
  public userEmail: string;
  public emailForm: UntypedFormGroup;
  public downloadForm: UntypedFormGroup;
  public codeVerificationForm: UntypedFormGroup;
  public codeRequested: boolean = false;
  public feedback: string;
  public errorFeedback: string;
  public requestErrorFeedback: string;
  public linkErrorFeedback: string;
  public isLoading: boolean;
  public userConfirmed: boolean;
  public parts = null;
  public hasProductionFinishedRunning = true;
  public productionJob: ProductionJobSharedDetail;
  public isValidLink: boolean = true;
  public hidePassword: boolean = true;
  public downloadingStatus: boolean[] = [];
  private readonly sharedId: string;

  constructor(
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private emailVerificationSdk: EmailVerificatoinSdk,
    private downloadSdk: DownloadSdk,
    private productionJobDownloadSharedManager: ProductionJobDownloadSharedManager,
  ) {
    super();
    this.emailForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]]
    });
    this.codeVerificationForm = this.formBuilder.group({
      codeVerification: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
    this.downloadForm = this.formBuilder.group({
      password: ['', [Validators.required]]
    });
    this.sharedId = this.route.snapshot.params['id'];
  }

  public ngOnInit(): void {
    this.isLoading = true;
    if (this.sharedId) {
      this.productionJobDownloadSharedManager.getProductionJobSharedDetail(this.sharedId)
        .pipe(
          tap((data) => {
            if (data.jobStatus === ProductionJobStatus.NeedValidation || data.jobStatus === ProductionJobStatus.PreRun) {
              this.isValidLink = false;
            }
            else {
              this.productionJob = data;
            }
          }),
          catchError(() => {
            this.isValidLink = false;
            return EMPTY;
          }),
          takeUntil(this.destroy$))
        .subscribe((data) => {
          if (data.jobStatus === ProductionJobStatus.NeedValidation || data.jobStatus === ProductionJobStatus.PreRun)
            this.isValidLink = false;
          else
            this.isLoading = false;
        });
    }
  }

  public requestCode() {
    this.isLoading = true;
    this.userEmail = this.emailForm.get('email').value;
    this.emailVerificationSdk.requestVerificationCode(this.sharedId, {
      caseId: this.productionJob.caseId,
      productionJobId: this.productionJob.productionJobId,
      email: this.userEmail
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.isLoading = false;
        this.codeRequested = true;
      },
      error: () => {
        this.isLoading = false;
        this.requestErrorFeedback = 'An error occurred requesting a code. Please try again.'
      }
    });
  }

  public verifyCode() {
    this.emailVerificationSdk.verifyCode(this.sharedId, {
      caseId: this.productionJob.caseId,
      productionJobId: this.productionJob.productionJobId,
      email: this.userEmail,
      verificationCode: this.codeVerificationForm.get('codeVerification').value
    }).pipe(
      tap(() => {
        this.isLoading = true;
        this.errorFeedback = null;
        this.feedback = null;
        this.codeRequested = true;
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.feedback = response.message;
        this.codeRequested = false;
        this.userConfirmed = true;
        this.isLoading = false;
      },
      error: (error) => {
        this.errorFeedback = 'Failed to verify email code.';
        if (error.status === 401) {
          this.errorFeedback += ' Unauthorized access. Please check your verification code.';
        } else if (error.status === 400) {
          this.errorFeedback += ' Bad request. Please verify your input.';
        } else {
          this.errorFeedback += ' An error occurred. Please try again later.';
        }
        this.isLoading = false;
      }
    });
  }

  public getDownloadParts() {
    if (this.productionJob?.hasPassword && this.downloadForm?.get('password').valid) {
      this.downloadSdk.getSharedProductionDownloadParts({
        sharedProductionJobId: this.sharedId,
        productionJobId: this.productionJob.productionJobId,
        emailAddress: this.userEmail,
        password: this.downloadForm.get('password').value,
      }).pipe(
        takeUntil(this.destroy$),
        tap(()=> {
          this.isLoading = true; 
          this.linkErrorFeedback=null;
        }),
        catchError((error) => {
          if (error?.status === 401) {
            this.linkErrorFeedback = 'The password you entered is incorrect. Please double-check and try again.';
          }
          else {
            this.linkErrorFeedback = 'An error occurred while requesting download links. Please try again later.';
          }
          this.isLoading = false;
          return of(null);
        })
      ).subscribe((response) => {
        this.hasProductionFinishedRunning = true;
        if (response != null) {
          if (response.status === 200) {
            this.parts = response.parts || null;
            this.initializeDownloadingStatus(this.parts?.length || 0);
          }
          else if (response.status === 206) {
            this.parts = [];
            this.hasProductionFinishedRunning = false;
          }
          else if (response.status === 401) {
            this.linkErrorFeedback = 'The password you entered is incorrect. Please double-check and try again.';
          }
          else {
            this.parts = response.parts || [];
            this.initializeDownloadingStatus(this.parts?.length || 0);
          }
        }
        this.isLoading = false;
      });
    }
  }

  public downloadPart(part: string, partIndex: number): void {
    if (this.downloadingStatus[partIndex]) 
      this.downloadingStatus[partIndex] = false;

    this.downloadSdk.getSharedProductionDownloadUrl({
      sharedProductionJobId: this.sharedId,
      productionJobId: this.productionJob.productionJobId,
      emailAddress: this.userEmail,
      password: this.downloadForm.get('password').value,
      part,
      index: partIndex
    }).pipe(
      takeUntil(this.destroy$),
      tap(()=> {
        this.isLoading = true; 
        this.downloadingStatus[partIndex] = true;
      })
    ).subscribe((response) => {
      window.open(response.downloadUrl, '_blank');
      this.isLoading = false;
    });
  }


  private initializeDownloadingStatus(numberOfParts: number): void {
    this.downloadingStatus = Array(numberOfParts).fill(false);
  }
}
